/**
 * D5 (I27 / I28) — an open OnlyOffice editor follows ITS document's session events over the
 * realtime socket, whatever org it opened the file from.
 *
 * Owner and `.shared`/`.chain` recipients edit one OnlyOffice session (keyed by the SERVED
 * file), but the old office frames were tenant broadcasts: a recipient in another org never
 * heard the owner's save conflict or a Discard. Here the editor registers the id it opened
 * the file by (`ref` — a native, pointer or compound id) with its WOPI access token; the
 * socket subscribes (`oo-doc-subscribe`) and the server admits it to the SERVED document.
 * Frames for that document (`oo-doc`) are re-dispatched as the same `papan:oo-*` window
 * events the editor already handles — with `fileId` = the editor's own `ref`, so nothing
 * downstream changes. A tenant `oo-*` frame for a `ref` already followed by its document is
 * dropped (it is the same event, twice).
 *
 * On a RECONNECT every registration re-subscribes and the editor gets `papan:oo-resync`
 * (I28): events sent while the socket was down are lost, so it re-checks (re-mint → a
 * Discarded key moves; re-read the draft status).
 */

interface Registration {
  ref: string
  /** The latest token any holder registered with (re-mints keep it live). */
  token: string
  /** The served document key the server admitted, once acknowledged. */
  doc: string | null
  /** The editors following `ref` — two editors of one file in a tab share the socket's
   *  one subscription; it ends when the LAST of them lets go. */
  holders: Set<unknown>
  /** Subscribes sent on this socket still awaiting their answer (a refusal answers too). */
  pending: number
  /** When the last of them was sent — a subscribe unanswered past PENDING_WAIT_MS (the
   *  server drops a rate-limited control frame silently) stops holding the sweep.
   *  Accepted residual: an answer that DOES come, later than that, for the same document
   *  under another id may find its subscription already ended by the sweep — that editor
   *  hears its document again after the next reconnect (its tenant frames are unaffected
   *  only while it is unacknowledged). */
  pendingSince: number
}

const PENDING_WAIT_MS = 10_000

/**
 * One office-document channel: the registrations of the editors following documents over
 * ONE socket. Two exist — the drive's session socket (`/ws`, `sessionOfficeDocChannel`) and
 * a share-link visitor's office socket (`/ws/office`, `shareOfficeDocChannel`, I33) — with
 * the same behaviour (holders, pending answers, orphan sweep, resync on reconnect).
 */
export function createOfficeDocChannel() {
  const regs = new Map<string, Registration>()
  let sender: ((msg: Record<string, unknown>) => void) | null = null
  let everAttached = false
  /**
   * Documents the server may still hold for this socket that no editor follows any more (an
   * editor closed, or closed before its answer came). Ended once NO subscribe is awaiting its
   * answer: until then an unanswered one may be the same document under another id (a
   * pointer id and a compound id) — the server keeps ONE subscription per document, and
   * ending it would leave that editor deaf on both channels. A subscribe that is never
   * answered (a rate-limited control frame) holds the sweep only until the socket reconnects.
   */
  const orphans = new Set<string>()
  let sweepTimer: ReturnType<typeof setTimeout> | null = null

  function subscribe(r: Registration): void {
    if (!sender) return
    r.pending += 1
    r.pendingSince = Date.now()
    sender({ type: 'oo-doc-subscribe', token: r.token, ref: r.ref })
  }

  const awaitingAnswer = (x: Registration) => x.pending > 0 && Date.now() - x.pendingSince < PENDING_WAIT_MS

  function sweepOrphans(): void {
    if (!sender || orphans.size === 0) return
    if ([...regs.values()].some(awaitingAnswer)) {
      // Retry once the wait bound has passed (an answer that never comes must not hold it).
      if (!sweepTimer) sweepTimer = setTimeout(() => { sweepTimer = null; sweepOrphans() }, PENDING_WAIT_MS)
      return
    }
    for (const doc of orphans) {
      if (![...regs.values()].some((x) => x.doc === doc)) sender({ type: 'oo-doc-unsubscribe', doc })
    }
    orphans.clear()
  }

  /**
   * Follow the document an editor (`holder` — any stable identity, one per editor) opened as
   * `ref`, admitted by `token` (its WOPI access token — call again after every re-mint, with
   * the same holder, so a reconnect re-subscribes with a live one). Returns the unregister
   * function for that holder.
   */
  function register(ref: string, token: string, holder: unknown = Symbol('office-doc')): () => void {
    let r = regs.get(ref)
    if (!r) {
      r = { ref, token, doc: null, holders: new Set(), pending: 0, pendingSince: 0 }
      regs.set(ref, r)
      subscribe(r)
    } else if (r.token !== token) {
      r.token = token
      subscribe(r)
    }
    r.holders.add(holder)
    const mine = r
    return () => {
      if (regs.get(ref) !== mine || !mine.holders.delete(holder) || mine.holders.size > 0) return
      regs.delete(ref)
      if (mine.doc) orphans.add(mine.doc)
      sweepOrphans()
    }
  }

  /** Socket open (`send`) / closed (`null`). On a RE-open every registration re-subscribes and
   *  its editor is told to re-check (`papan:oo-resync`). */
  function attachSender(send: ((msg: Record<string, unknown>) => void) | null): void {
    sender = send
    // Either way the previous socket — and every subscription the server held for it, and
    // every answer it still owed — is gone. (Reset on attach too: a socket torn down by
    // closeSocket, which drops its onclose, may never have reported null.)
    for (const r of regs.values()) { r.doc = null; r.pending = 0 }
    orphans.clear()
    if (sweepTimer) { clearTimeout(sweepTimer); sweepTimer = null }
    if (!send) return
    const reconnect = everAttached
    everAttached = true
    for (const r of regs.values()) {
      subscribe(r)
      if (reconnect && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('papan:oo-resync', { detail: { fileId: r.ref } }))
      }
    }
  }

  /** The server's answer to `oo-doc-subscribe`. */
  function onSubscribed(msg: { ref?: unknown; doc?: unknown; ok?: unknown }): void {
    if (typeof msg.ref !== 'string') return
    const r = regs.get(msg.ref)
    const doc = msg.ok === true && typeof msg.doc === 'string' ? msg.doc : null
    if (r) {
      r.doc = doc
      r.pending = Math.max(0, r.pending - 1)
    } else if (doc) {
      // Every editor let go before the answer came: a subscription nobody holds (see orphans).
      orphans.add(doc)
    }
    sweepOrphans()
  }

  /** A document frame: re-dispatched to every editor following it, as its own `papan:oo-*`. */
  function onEvent(msg: { doc?: unknown; event?: unknown }): void {
    if (typeof window === 'undefined' || typeof msg.doc !== 'string') return
    const ev = msg.event as { type?: unknown; fileId?: unknown; entryFileId?: unknown } | undefined
    if (!ev || typeof ev.type !== 'string' || !ev.type.startsWith('oo-')) return
    for (const r of regs.values()) {
      if (r.doc !== msg.doc) continue
      // #823: a shortcut the org binned — only the editor opened through that ENTRY closes (as an `oo-closed`); its own
      // type, so a tab from before this change ignores it instead of closing every editor of the document
      if (ev.type === 'oo-entry-closed') {
        if (ev.entryFileId !== r.ref) continue
        window.dispatchEvent(new CustomEvent('papan:oo-closed', { detail: { ...ev, type: 'oo-closed', fileId: r.ref, docFileId: ev.fileId } }))
        continue
      }
      // …and an API instance still on the previous release sends it as a plain `oo-closed` naming the entry
      if (typeof ev.entryFileId === 'string' && ev.entryFileId !== r.ref) continue
      window.dispatchEvent(new CustomEvent(`papan:${ev.type}`, { detail: { ...ev, fileId: r.ref, docFileId: ev.fileId } }))
    }
  }

  /** Is `fileId` an editor already following its document? Its tenant `oo-*` frame is then a
   *  duplicate of the document frame and is dropped. */
  function isFollowed(fileId: unknown): boolean {
    return typeof fileId === 'string' && !!regs.get(fileId)?.doc
  }

  /** Tests only. */
  function reset(): void {
    regs.clear()
    orphans.clear()
    if (sweepTimer) { clearTimeout(sweepTimer); sweepTimer = null }
    sender = null
    everAttached = false
  }

  return { register, attachSender, onSubscribed, onEvent, isFollowed, reset }
}
export type OfficeDocChannel = ReturnType<typeof createOfficeDocChannel>

/** The drive's session socket (`/ws` — useRealtime). */
export const sessionOfficeDocChannel = createOfficeDocChannel()
/** A share-link visitor's office socket (`/ws/office` — useShareOfficeSocket). */
export const shareOfficeDocChannel = createOfficeDocChannel()

// The session channel under its established names (useRealtime, tests).
export const registerOfficeDoc = sessionOfficeDocChannel.register
export const attachOfficeDocSender = sessionOfficeDocChannel.attachSender
export const onOfficeDocSubscribed = sessionOfficeDocChannel.onSubscribed
export const onOfficeDocEvent = sessionOfficeDocChannel.onEvent
/** Is `fileId` an editor already following its document on EITHER channel? Its tenant `oo-*`
 *  frame is then a duplicate of the document frame and is dropped (a signed-in share visitor
 *  holds the drive socket AND the share office socket — the frame would dispatch twice). */
export const isFollowedOfficeDoc = (fileId: unknown): boolean =>
  sessionOfficeDocChannel.isFollowed(fileId) || shareOfficeDocChannel.isFollowed(fileId)
/** Tests only. */
export function __resetOfficeDocs(): void {
  sessionOfficeDocChannel.reset()
  shareOfficeDocChannel.reset()
}
